import { createContext, useContext } from 'react';
import { type ApplicationCharttViewID } from './ApplicationChart';
import { type DepositPaymentsPerDayViewID } from './DepositPaymentsPerDay';
import { type LatestApplicationTableViewID } from './LatestApplicationTable';
import { type LeadChartViewID } from './LeadChart';
import { type PopularApplicationVariantChartViewID } from './PopularApplicationVariantChart';
import { type PopularLeadEventViewID } from './PopularLeadEventChart';
import { type PopularLeadVariantChartViewID } from './PopularLeadVariantChart';
import { type StatisticsViewID } from './Statistics';
import { type TopSalesManWithApplicationViewID } from './TopSalesManWithApplication';
import { type TopSalesManWithLeadViewID } from './TopSalesManWithLead';
import { type TopSalesManWithLoanViewID } from './TopSalesManWithLoan';

export type ViewIDs =
    | typeof StatisticsViewID
    | typeof ApplicationCharttViewID
    | typeof PopularApplicationVariantChartViewID
    | typeof PopularLeadEventViewID
    | typeof PopularLeadVariantChartViewID
    | typeof LeadChartViewID
    | typeof DepositPaymentsPerDayViewID
    | typeof TopSalesManWithApplicationViewID
    | typeof TopSalesManWithLoanViewID
    | typeof TopSalesManWithLeadViewID
    | typeof LatestApplicationTableViewID;

export type DashboardContextState = {
    displayCurrentMonth: boolean;
    displayLastMonth: boolean;
    visible?: { [key in ViewIDs]?: boolean };
};

export type DashboardContextValue = {
    state: DashboardContextState;
    setDisplayCurrentMonth: (display: boolean) => void;
    setDisplayLastMonth: (display: boolean) => void;

    /**
     * Updates the visibility status of individual cards, which helps determine
     * whether to display or hide or arrange the group or section that contains these cards.
     */
    setVisible: (updates: { [viewID in ViewIDs]?: boolean }) => void;
};

const DashboardContext = createContext<DashboardContextValue | null>(null);

export const useDashboardContext = () => {
    const context = useContext(DashboardContext);

    if (!context) {
        throw new Error('Dashboard context missing');
    }

    return context;
};

export default DashboardContext;
